import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import "./Header.css";
// Statically imported hero image used as a last-resort fallback so the hero
// never renders photo-less if the require.context set below is ever empty
// (e.g. the folder is emptied). Note: the intermittent blank hero seen in the
// CRA dev server is a separate dev-only issue (the dev server can transiently
// resolve asset modules to undefined); the production build inlines every image
// URL as a string literal, so it cannot occur in the deployed site.
import fallbackHeroImage from "../assets/images/homepage-header/img1.jpeg";

// Dynamically import all images from the homepage-header folder.
// Case-insensitive so uppercase extensions (e.g. .JPG) are included; without the
// `i` flag those files are silently excluded and never appear in the rotation.
const importAll = (r) => r.keys().map(r);
const images = importAll(
  require.context(
    "../assets/images/homepage-header",
    false,
    /\.(jpe?g|png|svg)$/i
  )
).filter(Boolean);

const getRandomImage = (arr) =>
  arr[Math.floor(Math.random() * arr.length)] || fallbackHeroImage;

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Header = () => {
  // Pick the hero photo synchronously on first render (not in an effect) so the
  // background is present on the initial paint. An effect-based pick leaves the
  // first paint with no image and, under StrictMode/effect timing, could
  // intermittently leave the hero blank.
  const [headerImage] = useState(() => getRandomImage(images));
  const [scrolled, setScrolled] = useState(false);

  // The scroll hint is only a hint: once the visitor starts scrolling, fade it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToContent = () => {
    const el = document.getElementById("services");
    if (el) {
      el.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "start",
      });
    }
  };

  return (
    <header
      className="hero"
      style={{
        backgroundImage: headerImage ? `url(${headerImage})` : "none",
      }}
    >
      <div className="hero__scrim" />
      <div className="hero__inner">
        <p className="hero__eyebrow">Remote powerlifting coaching</p>
        <h1 className="hero__title">
          Build a bigger <span className="hero__accent">total.</span>
        </h1>
        <p className="hero__subtitle">
          Individualized programming, daily coaching, and professional meet-day
          handling. From your first meet to the national platform.
        </p>
        <div className="hero__actions">
          <Link to="/#contact" className="btn-primary btn--lg">
            Apply for coaching
          </Link>
          <Link to="/#services" className="btn-ghost">
            See how it works
          </Link>
        </div>
      </div>

      <button
        type="button"
        className={`hero__scroll ${scrolled ? "is-hidden" : ""}`}
        onClick={scrollToContent}
        aria-label="Scroll to content"
        tabIndex={scrolled ? -1 : 0}
      >
        <FaChevronDown aria-hidden="true" />
      </button>
    </header>
  );
};

export default Header;
